import "./monitoringView.css";

import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from "reactstrap";
import { DetailGrid } from "../detailGrid/detailGrid";
import { DistributionDonut } from "../../../clientDashboard/components/DistributionDonut";
import { FilterBar } from "../../../clientDashboard/components/FilterBar";
import { FilterSection } from "../filterSection/FilterSection";
import { MainList } from "../mainList/mainList";
import PropTypes from "prop-types";
import { humanize } from "../../../utils";
import { useState } from "react";

// Donut diameter (px). Overview spans the full rail; the per-group grid packs
// two per row, so its donuts are smaller. Both under the shared 172 default.
const DONUT_SIZE = 140;
const DETAIL_DONUT_SIZE = 110;

// Removable chips for the soft filters set by clicking a status dot, a donut
// slice, or a group. The status chip stays up while a status is drilled into
// its substatuses, so it always offers a way back out of the drill.
const ActiveFilterChips = ({ activeFilters }) => {
  const {
    softGroupingObject,
    setSoftGroupingObject,
    softSelectedStatus,
    setSoftSelectedStatus,
    softSelectedSubStatus,
    setSoftSelectedSubStatus,
    softSelectedSubStatusColor,
    setSelectedStatus,
    getStatusColor,
    generateCombinationString,
  } = activeFilters;

  if (!softGroupingObject && !softSelectedStatus && !softSelectedSubStatus)
    return null;

  return (
    <span className="monitoring-view__chips">
      {softGroupingObject && (
        <button
          type="button"
          className="monitoring-view__chip"
          onClick={() => setSoftGroupingObject(null)}
        >
          {generateCombinationString(softGroupingObject)}
          <i className="fa-solid fa-xmark" />
        </button>
      )}
      {softSelectedStatus && (
        <button
          type="button"
          className="monitoring-view__chip"
          style={{ borderColor: getStatusColor(softSelectedStatus) }}
          onClick={() => {
            setSoftSelectedStatus(null);
            setSoftSelectedSubStatus(null);
            setSelectedStatus(undefined);
          }}
        >
          {humanize(softSelectedStatus)}
          <i className="fa-solid fa-xmark" />
        </button>
      )}
      {softSelectedSubStatus && (
        <button
          type="button"
          className="monitoring-view__chip"
          style={{ borderColor: softSelectedSubStatusColor }}
          onClick={() => setSoftSelectedSubStatus(null)}
        >
          {humanize(softSelectedSubStatus)}
          <i className="fa-solid fa-xmark" />
        </button>
      )}
    </span>
  );
};

/*
 * Single monitoring view: a sticky left rail (status donut + activity toggles +
 * group-by) beside the full-width site/date list with its own filter bar. The
 * former Operations and Overview tabs merged into one screen; they always shared
 * the same filter state, so nothing is lost.
 */
export const MonitoringView = ({
  scopeControls,
  overviewBack,
  overviewSegments,
  onOverviewSelect,
  filterSiteName,
  setFilterSiteName,
  flagCount,
  filterActiveFlags,
  setFilterActiveFlags,
  commentCount,
  filterUnreadComments,
  setFilterUnreadComments,
  filterClientAction,
  setFilterClientAction,
  filterHasResults,
  setFilterHasResults,
  activeFilters,
  filterSectionProps,
  mainListProps,
  hasGroups,
  detailGridProps,
}) => {
  const groupCount = Object.values(filterSectionProps.groupingObject).filter(
    (values) => values?.length
  ).length;
  const [isGroupByOpen, setIsGroupByOpen] = useState(false);

  return (
    <div className="monitoring-view cf-split">
      <aside className="cf-split__aside">
        {/* Scope + Overview stay in view while the list scrolls (R1-16). */}
        <div className="monitoring-view__railtop">
          <div className="cf-filters monitoring-view__scope">
            {scopeControls}
          </div>
          <DistributionDonut
            title="Overview"
            headerAction={overviewBack}
            segments={overviewSegments}
            unit="dates"
            size={DONUT_SIZE}
            onSelect={onOverviewSelect}
          />
        </div>
        <Accordion
          open={isGroupByOpen ? "groupby" : ""}
          toggle={() => setIsGroupByOpen((open) => !open)}
          className="monitoring-view__groupby"
        >
          <AccordionItem>
            <AccordionHeader targetId="groupby">
              <i className="fa-solid fa-cubes me-2" /> Group by
              {groupCount > 0 && (
                <span className="cf-groupby-count ms-2">{groupCount}</span>
              )}
            </AccordionHeader>
            <AccordionBody accordionId="groupby">
              <FilterSection {...filterSectionProps} />
            </AccordionBody>
          </AccordionItem>
        </Accordion>
        {hasGroups && (
          <DetailGrid {...detailGridProps} size={DETAIL_DONUT_SIZE} />
        )}
      </aside>
      <div className="cf-split__main">
        <FilterBar
          query={filterSiteName}
          onQuery={setFilterSiteName}
          placeholder="Filter by site"
          extra={<ActiveFilterChips activeFilters={activeFilters} />}
          trailing={
            <>
              <label className="cf-actionfilter">
                <input
                  type="checkbox"
                  checked={filterActiveFlags}
                  onChange={(event) =>
                    setFilterActiveFlags(event.target.checked)
                  }
                />
                <i className="fa-solid fa-flag" /> Flags {flagCount}
              </label>
              <label className="cf-actionfilter">
                <input
                  type="checkbox"
                  checked={filterUnreadComments}
                  onChange={(event) =>
                    setFilterUnreadComments(event.target.checked)
                  }
                />
                <i className="fa-solid fa-comment-dots" /> Comments{" "}
                {commentCount}
              </label>
              <label className="cf-actionfilter">
                <input
                  type="checkbox"
                  checked={filterClientAction}
                  onChange={(event) =>
                    setFilterClientAction(event.target.checked)
                  }
                />
                <i className="fa-solid fa-wrench" /> Client action required
              </label>
              <label className="cf-actionfilter">
                <input
                  type="checkbox"
                  checked={filterHasResults}
                  onChange={(event) =>
                    setFilterHasResults(event.target.checked)
                  }
                />
                <i className="fa-solid fa-earth-americas" /> Results available
              </label>
            </>
          }
        />
        <MainList {...mainListProps} />
      </div>
    </div>
  );
};

const activeFiltersShape = PropTypes.object;

ActiveFilterChips.propTypes = {
  activeFilters: activeFiltersShape.isRequired,
};

MonitoringView.propTypes = {
  scopeControls: PropTypes.node.isRequired,
  overviewBack: PropTypes.node,
  overviewSegments: PropTypes.array.isRequired,
  onOverviewSelect: PropTypes.func.isRequired,
  filterSiteName: PropTypes.string.isRequired,
  setFilterSiteName: PropTypes.func.isRequired,
  flagCount: PropTypes.number.isRequired,
  filterActiveFlags: PropTypes.bool.isRequired,
  setFilterActiveFlags: PropTypes.func.isRequired,
  commentCount: PropTypes.number.isRequired,
  filterUnreadComments: PropTypes.bool.isRequired,
  setFilterUnreadComments: PropTypes.func.isRequired,
  filterClientAction: PropTypes.bool.isRequired,
  setFilterClientAction: PropTypes.func.isRequired,
  filterHasResults: PropTypes.bool.isRequired,
  setFilterHasResults: PropTypes.func.isRequired,
  activeFilters: activeFiltersShape.isRequired,
  filterSectionProps: PropTypes.object.isRequired,
  mainListProps: PropTypes.object.isRequired,
  hasGroups: PropTypes.bool.isRequired,
  detailGridProps: PropTypes.object.isRequired,
};
